import * as z from 'zod/mini'
import { AuthRegisterEntityBody, entityFields, refineWithKey, userFields } from '@seiri/shared/common'
import { requiredFormDate, requiredText } from './form-schema'

const mustAccept = z.boolean().check(refineWithKey('validation.consentRequired', (value: boolean) => value))

/**
 * The owner's sign-up page: their account and their premise in one form. It differs from
 * `AuthRegisterEntityBody` in the picker's `Date`, the two consents and the field names of the
 * company section, hence its own schema and `toAuthRegisterEntityBody`. The field rules are the
 * API's, plus what only the form requires (names, consents).
 */
export const registrationFormModel = z.object({
  email: userFields.email,
  phone: userFields.phone.check(
    refineWithKey('validation.phoneCharacters', (value: string) => /^[0-9+/ -]*$/.test(value)),
    refineWithKey('validation.phoneMinLength', (value: string) => value.length === 0 || value.length >= 4),
  ),
  firstName: userFields.firstName.check(requiredText),
  lastName: userFields.lastName.check(requiredText),
  password: userFields.newPassword,
  birthDate: requiredFormDate(),
  gender: userFields.gender,
  privacyPolicy: mustAccept,
  gdpr: mustAccept,
  premiseName: entityFields.name,
  companyName: z.string().check(z.maxLength(255)),
  companyId: z.string().check(z.maxLength(255)),
  companyTaxId: z.string().check(z.maxLength(255)),
  companyIBAN: z.string().check(z.maxLength(255)),
})
export type RegistrationFormModel = z.infer<typeof registrationFormModel>

/** `timezone` is the browser's for now - the premise is created where its owner signs up. */
export const toAuthRegisterEntityBody = (model: RegistrationFormModel, timezone: string): AuthRegisterEntityBody => {
  if (!model.birthDate) throw new Error('birthDate is empty; the form should not have submitted')
  return {
    email: model.email,
    phone: model.phone,
    firstName: model.firstName,
    lastName: model.lastName,
    password: model.password,
    birthDate: model.birthDate.toISOString(),
    gender: model.gender,
    name: model.premiseName,
    companyName: model.companyName,
    companyId: model.companyId,
    vatId: model.companyTaxId,
    iban: model.companyIBAN,
    timezone,
  }
}
